import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import Modal from 'react-modal';
import { StickyNote } from 'lucide-react';
import { hideAddIncidentInternalNoteModal } from '@@/features/modal';
import { useAddIncidentCommentMutation } from '@@/services/incident';
import { RootState } from '@@/store';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import StandardInput from '@@/components/Inputs/StandardInput';

const schema = yup
  .object({
    body: yup.string().trim().required('Entrez votre note interne'),
  })
  .required();

const AddIncidentInternalNoteModal: React.FC<{ incidentId: string }> = ({
  incidentId,
}) => {
  const dispatch = useDispatch();
  const showAddIncidentInternalNoteModal = useSelector(
    (state: RootState) => state.modal.showAddIncidentInternalNoteModal,
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{
    body: string;
    template?: string;
  }>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const closeModal = useCallback(() => {
    dispatch(hideAddIncidentInternalNoteModal());
    reset();
  }, [dispatch, reset]);

  const [addIncidentComment, { isSuccess }] = useAddIncidentCommentMutation();

  const onSubmit = useCallback(
    (data: { body: string }) => {
      if (incidentId) {
        addIncidentComment({
          incidentId,
          body: data.body,
          isInternal: true,
        });
      }
    },
    [addIncidentComment, incidentId],
  );

  useEffect(() => {
    return closeModal();
  }, [closeModal]);

  useEffect(() => {
    if (isSuccess) {
      closeModal();
      reset();
      toast.success('Votre note interne a été publiée', {
        position: 'top-right',
      });
    }
  }, [isSuccess, reset, closeModal]);

  return (
    <Modal
      isOpen={showAddIncidentInternalNoteModal}
      onRequestClose={closeModal}
      className="absolute bg-white py-6 px-8 border border-neutral-300 rounded-modal shadow-modal top-20 left-8 sm:top-1/2 sm:left-1/2 sm:transform sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[600px] max-h-[90vh] overflow-y-auto"
      contentLabel="Show Add Incident Comment Modal"
      ariaHideApp={false}
    >
      <div className="flex items-center justify-center gap-2 mb-6">
        <StickyNote size={24} className="text-warning-600" />
        <h2 className="text-heading-md text-neutral-900">Ajouter une note interne</h2>
      </div>

      <hr className="mb-6 border-neutral-200" />

      <form>
        <StandardInput
          register={register}
          id="body"
          label="Note interne *"
          type="textarea"
          placeholder="Note interne"
          error={errors.body}
        />
      </form>

      <div className="flex justify-end mt-6 pt-6 border-t border-neutral-200">
        <button
          className="px-6 py-3 rounded-button bg-primary-600 hover:bg-primary-700 text-white shadow-button transition-colors duration-200 text-body-sm font-medium"
          onClick={handleSubmit(onSubmit)}
        >
          Valider
        </button>
      </div>
    </Modal>
  );
};

export default AddIncidentInternalNoteModal;
